import { z } from 'zod';
import {
    ResourceLibraryNewsBaseItem,
    ResourceLibraryNewsItem,
} from '../types/resourceLibraryItems';
import {
    GetResourceLibraryNewsItemSchema,
    ResourceLibraryNewsBaseItemSchema,
    ResourceLibraryNewsItemSchema,
} from '../schemas/resourceLibraryNewsItemZodSchemas';
import {
    ProductGroupSchema,
    ProductApplicationSchema,
    NavigationNodeSchema,
} from '../schemas/resourceLibraryBaseZodSchemas';

export function hydrateResourceLibraryNewsBaseItem(
    item: z.infer<typeof ResourceLibraryNewsBaseItemSchema>
): ResourceLibraryNewsBaseItem {
    const newsBaseItem: ResourceLibraryNewsBaseItem = {
        kind: 'news',
        id: item.id,
        key: item.key,
        name: item.name,
        description: item.description ?? '', // Description is nullable
        brandId: item.brand ? item.brand.id : null,
        productGroupIds: item.productGroup
            ? item.productGroup.map((pg: z.infer<typeof ProductGroupSchema>) => pg.id)
            : [],
        productApplicationIds: item.productApplication
            ? item.productApplication.map((pa: z.infer<typeof ProductApplicationSchema>) => pa.id)
            : [],
        resourceTypeId: item.resourceType.id,
        navigationScopeIds: item.navigationScope
            ? item.navigationScope.map((ns: z.infer<typeof NavigationNodeSchema>) => ns.id)
            : [],
        exclusiveNavigationScopeIds: item.exclusiveNavigationScope
            ? item.exclusiveNavigationScope.map(
                  (ens: z.infer<typeof NavigationNodeSchema>) => ens.id
              )
            : [],
        publicationDate: item.publicationDate,
        thumbnailUrl: item.thumbnailUrl,
        coverImageUrl: item.coverImage?.fullpath ?? null,
    };

    return newsBaseItem;
}

export function hydrateResourceLibraryNewsItem(
    item: z.infer<typeof ResourceLibraryNewsItemSchema>
): ResourceLibraryNewsItem {
    const newsItem = hydrateResourceLibraryNewsBaseItem(item);
    return {
        ...newsItem,
        body: item.body,
    };
}

/**
 * Transforms and validates the response from the getResourceLibraryNewsItem GraphQL query
 * to a simplified ResourceLibraryNewsItem type.
 *
 * @param data The response data from the getResourceLibraryNewsItem query
 * @returns An array of valid ResourceLibraryNewsItem objects
 */
export function transformResourceLibraryNewsItemResponse(
    data: unknown // GetResourceLibraryNewsItemQuery - use unknown for jest tests be able to
    // pass invalid data
): ResourceLibraryNewsItem | null {
    // Parse and validate the data using the Zod schema
    const parsedResult = GetResourceLibraryNewsItemSchema.safeParse(data);

    if (!parsedResult.success) {
        console.error(
            'Validation failed for getResourceLibraryNewsItem:',
            parsedResult.error.errors
        );
        return null;
    }

    const item = parsedResult.data.getResourceLibraryNewsItem;
    return hydrateResourceLibraryNewsItem(item);
}
